import { memo, useMemo, useState } from 'react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { usePortfolioData } from './usePortfolioData';
import { PROGRAM_MAPPING } from '../../../utils/dataProcessor/constants.js';

// SP01–SP13 in display order
const SP_CODES = [
  'SP01', 'SP02', 'SP03', 'SP04', 'SP05', 'SP06',
  'SP07', 'SP08', 'SP09', 'SP10', 'SP11', 'SP12', 'SP13',
];

/**
 * Interpolate between white (#fff) and CGIAR green (#2d7f3f) based on a 0–1 ratio.
 */
function intensityColor(ratio) {
  if (ratio <= 0) return '#f3f4f6';
  const r = Math.round(255 + (45 - 255) * ratio);
  const g = Math.round(255 + (127 - 255) * ratio);
  const b = Math.round(255 + (63 - 255) * ratio);
  return `rgb(${r},${g},${b})`;
}

/**
 * CrossProgramHeatmap
 * Grid heatmap showing cross-program collaboration intensity.
 * Each cell (row program × column program) is colored by the number of results where
 * both programs appear in the Contributing Initiatives field.
 * Diagonal cells represent results attributed to a single program.
 */
export const CrossProgramHeatmap = memo(function CrossProgramHeatmap({ data: dataProp }) {
  const { filteredResults } = usePortfolioData();
  const [tooltip, setTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Build the co-occurrence matrix from filteredResults
  const { matrix, activeCodes, maxCount } = useMemo(() => {
    const counts = {};

    filteredResults.forEach(r => {
      // Collect all program codes for this result from initiative + contributingInitiatives
      const codeSet = new Set();

      const addCodes = (raw) => {
        if (!raw) return;
        // Normalise: arrays → join, non-strings → coerce
        const str = Array.isArray(raw) ? raw.join(', ') : String(raw);
        str.split(/[,;]/).forEach(part => {
          const match = part.trim().match(/^(SP\d+|INIT-\d+)/);
          if (!match) return;
          const raw = match[1];
          // Normalise legacy INIT codes via PROGRAM_MAPPING
          if (raw.startsWith('SP')) {
            codeSet.add(raw);
          } else {
            const name = PROGRAM_MAPPING[raw];
            const sp = name?.match(/^(SP\d+)/)?.[1];
            if (sp) codeSet.add(sp);
          }
        });
      };

      addCodes(r.initiative);
      addCodes(r.contributingInitiatives || r.contributing_initiatives);

      const codeArr = [...codeSet].filter(c => SP_CODES.includes(c));
      if (codeArr.length === 0) return;

      // Count every ordered pair (including self-pairs for diagonal)
      codeArr.forEach(src => {
        codeArr.forEach(tgt => {
          const key = `${src}||${tgt}`;
          counts[key] = (counts[key] || 0) + 1;
        });
      });
    });

    // Determine which SP codes actually appear in the data
    const activeCodes = SP_CODES.filter(c =>
      Object.keys(counts).some(k => k.startsWith(`${c}||`) || k.endsWith(`||${c}`))
    );

    // Build matrix rows
    const matrix = activeCodes.map(src =>
      activeCodes.map(tgt => counts[`${src}||${tgt}`] || 0)
    );

    // Max off-diagonal count for colour scaling
    let maxCount = 1;
    activeCodes.forEach((src, i) => {
      activeCodes.forEach((tgt, j) => {
        if (i !== j && matrix[i][j] > maxCount) maxCount = matrix[i][j];
      });
    });

    return { matrix, activeCodes, maxCount };
  }, [filteredResults]);

  if (!activeCodes || activeCodes.length < 2) {
    return (
      <ChartWrapper title="Cross-Program Collaboration Heatmap">
        <NoChartData
          message="Insufficient cross-program data"
          detail="This heatmap shows collaboration intensity between programs. At least two programs with Contributing Initiatives data are required."
        />
      </ChartWrapper>
    );
  }

  const exportData = [];
  activeCodes.forEach((src, i) => {
    activeCodes.forEach((tgt, j) => {
      if (i < j && matrix[i][j] > 0) {
        exportData.push({
          'Program A': src,
          'Program B': tgt,
          'Co-contributions': matrix[i][j],
        });
      }
    });
  });

  const cellSize = Math.max(28, Math.min(44, Math.floor(560 / activeCodes.length)));

  return (
    <ChartWrapper
      title="Cross-Program Collaboration Heatmap"
      info="Colour intensity shows how many results involve both programs (via the Contributing Initiatives field). Darker cells indicate stronger collaboration. The diagonal represents single-program results."
      chartData={exportData}
      sourceResults={filteredResults}
      exportFilename="cross-program-heatmap"
    >
      <div className="overflow-x-auto">
        <div style={{ minWidth: activeCodes.length * cellSize + 56 }}>
          {/* Column headers */}
          <div className="flex" style={{ marginLeft: 52 }}>
            {activeCodes.map(code => (
              <div
                key={code}
                style={{
                  width: cellSize, height: 40, display: 'flex',
                  alignItems: 'flex-end', justifyContent: 'center',
                  paddingBottom: 4, flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontSize: 10, fontWeight: 600, color: '#555',
                    writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                    lineHeight: 1,
                  }}
                >
                  {code}
                </span>
              </div>
            ))}
          </div>

          {/* Rows */}
          {activeCodes.map((src, i) => (
            <div key={src} className="flex items-center">
              {/* Row label */}
              <div
                style={{
                  width: 52, flexShrink: 0, textAlign: 'right',
                  paddingRight: 6, fontSize: 10, fontWeight: 600, color: '#555',
                }}
              >
                {src}
              </div>
              {/* Cells */}
              {activeCodes.map((tgt, j) => {
                const count = matrix[i][j];
                const isDiag = i === j;
                const ratio = isDiag ? 0 : (count / maxCount);
                const bg = isDiag ? '#e5e7eb' : intensityColor(ratio);
                const textColor = ratio > 0.55 ? '#fff' : '#374151';

                return (
                  <div
                    key={tgt}
                    style={{
                      width: cellSize, height: cellSize, flexShrink: 0,
                      backgroundColor: bg,
                      border: '1px solid #f3f4f6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: count > 0 && !isDiag ? 'default' : 'default',
                      borderRadius: 2,
                    }}
                    onMouseEnter={(e) => {
                      if (count > 0) {
                        setTooltip({ src, tgt, count, isDiag });
                        setTooltipPos({ x: e.clientX, y: e.clientY });
                      }
                    }}
                    onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    {count > 0 && (
                      <span style={{ fontSize: cellSize < 36 ? 9 : 11, fontWeight: 600, color: textColor }}>
                        {count}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        <p className="text-xs text-gray-400">
          Cell value = number of results where both programs are listed as contributing initiatives
        </p>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span>Fewer</span>
          <div
            className="w-24 h-3 rounded"
            style={{ background: `linear-gradient(to right, ${intensityColor(0.05)}, ${intensityColor(1)})` }}
          />
          <span>More</span>
        </div>
      </div>

      {/* Floating tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{ left: tooltipPos.x + 12, top: tooltipPos.y - 36 }}
        >
          <div style={{
            background: '#fff', padding: '8px 12px', borderRadius: '8px',
            border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            fontSize: '13px', lineHeight: 1.6,
          }}>
            {tooltip.isDiag ? (
              <>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>{tooltip.src}</div>
                <div style={{ color: '#6b7280', fontSize: 12 }}>Single-program results: {tooltip.count}</div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>
                  {tooltip.src} &harr; {tooltip.tgt}
                </div>
                <div style={{ color: '#4b5563' }}>
                  Co-contributions: <strong>{tooltip.count.toLocaleString()}</strong>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </ChartWrapper>
  );
});
